import { Plus } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { Navigate } from 'react-router'

import cfoHero from '@/assets/marketing/images/cfo-hero.jpg'
import { Button } from '@/components/ui/button'
import { trackMarketingCta } from '@/lib/analytics'
import { useHostGuard } from '@/lib/hostGuard'
import { cn } from '@/lib/utils'
import { HubSpotFormEmbed } from './components/HubSpotFormEmbed'
import { NavBar } from './components/NavBar'
import { Reveal } from './components/Reveal'
import { SiteFooter } from './components/SiteFooter'
import { PRODUCT_NAV_ITEMS } from './content/productNavItems'
import {
  SITE_FOOTER_LEGAL_LINKS,
  SITE_FOOTER_LINK_COLUMNS,
  SITE_FOOTER_TAGLINE,
} from './content/siteFooterContent'

/* Standalone Cai AI CFO beta landing page — own header/footer, no MarketingLayout, same pattern as Bookkeeper.tsx. */

const HUBSPOT_PORTAL_ID = '246335544'
const HUBSPOT_FORM_ID = 'd7b06d21-1d1c-4b74-8971-4d4e150a91d4'
const HUBSPOT_REGION = 'na2'

const CONNECTS = ['Accounting system', 'Bank accounts', 'Fuel card', 'Factoring']

const CAPABILITIES = [
  { label: 'Monitors', body: 'Every transaction, invoice, and payment' },
  { label: 'Flags', body: 'Any potential concerns or risks' },
  { label: 'Plans', body: 'How you did last month, and what to do this month' },
  { label: 'Advises', body: 'Any financial decision, 24/7/365' },
]

// Bar heights (%), one per forecast week — index 7 (week 8) is the lowest, matching "Low week 8" below.
const FORECAST_BARS = [66, 73, 68, 80, 75, 84, 59, 50, 57, 70, 77, 82, 89]

const QUESTIONS = [
  'Which truck made me the least money last quarter?',
  'Can I cover payroll if that broker pays 30 days late?',
  "What's my cost per mile this month, and why did it go up?",
  'Should I pay cash for the trailer or finance it?',
  'If I bring on another truck, when will I break even?',
]

const FAQS: { question: string; answer: ReactNode }[] = [
  {
    question: 'What is Cai?',
    answer:
      "Cai makes AI-powered financial technology for the businesses that build, move, and fix the world around us. We ran our own fleet, so we build tools to drive down the costs that hit trucking hardest. In addition to Cai AI CFO, we offer solutions for two of trucking's biggest expenses: insurance and fuel.",
  },
  {
    question: 'What does a CFO do?',
    answer:
      "A Chief Financial Officer is the person who watches a company's money and decides what to do about it. Not the bookkeeper who records what happened, but the person who tells you what it means: where cash is going, what a truck really earns, whether you can afford the next one.",
  },
  {
    question: 'How does Cai use AI here?',
    answer:
      "AI reads your connected accounts, spots what's out of pattern, and shares recommendations. It works around the clock, which is the part a person can't do. What it doesn't do is act on your money without you: anything that moves cash waits for your approval, and you see the numbers behind every answer.",
  },
  {
    question: 'Does this replace my bookkeeper, accountant, or actual CFO?',
    answer:
      'Cai can work with whoever handles your books, or any accounting software you use. If you have a human CFO, Cai can give them a sounding board and a second set of eyes.',
  },
  {
    question: 'What happens after the free 3 months?',
    answer:
      "We'll tell you the price before the three months are up, and you decide then. There's no card on file during the beta, so nothing starts charging on its own.",
  },
  {
    question: 'What do I have to connect?',
    answer:
      'Like any CFO, Cai needs quality data about your fleet to give you quality advice. Connect accounts anywhere your money moves: Cai Bookkeeper, QuickBooks, your bank, your fuel card, your factoring company, etc.',
  },
]

function FaqItem({
  question,
  answer,
  open,
  onToggle,
}: {
  question: string
  answer: ReactNode
  open: boolean
  onToggle: () => void
}) {
  return (
    <div className="border-t border-white/16 last:border-b">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center justify-between gap-5 py-5.5 text-left"
      >
        <span className="text-lg font-bold tracking-[-0.015em] text-white">{question}</span>
        <Plus
          aria-hidden="true"
          className={cn(
            'text-brand-500 size-4 flex-none transition-transform duration-300',
            open && 'rotate-45'
          )}
        />
      </button>
      <div
        className={cn(
          'overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out',
          open ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <p className="max-w-[44ch] pb-5.5 leading-[1.65] text-white/70">{answer}</p>
      </div>
    </div>
  )
}

export function AiCfo() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const { redirecting, navigateTo } = useHostGuard('marketing')
  if (navigateTo) return <Navigate to={navigateTo} replace />
  if (redirecting) return null

  return (
    <div className="bg-ink-50 min-h-screen w-full shrink-0">
      <title>Cai | AI CFO</title>
      <meta
        name="description"
        content="While you run your fleet, your always-on AI CFO watches the money, flags what's wrong, and helps you plan your next move."
      />

      <div className="bg-ink-900">
        <NavBar
          theme="dark"
          navLinks={[
            { label: 'About', href: '/#about' },
            { label: 'Products', items: PRODUCT_NAV_ITEMS, active: true },
            { label: 'Blog', href: 'https://blog.cai.ai', external: true },
          ]}
          ctaLabel="Get on the list"
          ctaHref="#beta"
          onCtaClick={() => trackMarketingCta('cfo_nav')}
        />

        <section className="mx-auto grid max-w-(--maxw) grid-cols-2 items-center gap-14 px-5.5 py-24 max-lg:grid-cols-1">
          <Reveal>
            <span className="text-brand-500 font-mono text-xs font-bold tracking-[0.14em] uppercase">
              AI CFO · beta
            </span>
            <h1 className="h1-hero mt-5.5 max-w-[15ch] text-pretty text-white">
              You run the fleet. Cai runs the finances.
            </h1>
            <p className="mt-6.5 max-w-120 text-xl leading-[1.6] text-pretty text-white/70">
              Your always-on AI CFO watches the money, flags what&apos;s wrong, and helps you plan your next
              move.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <Button size="lg" href="#beta" onClick={() => trackMarketingCta('cfo_hero')}>
                Get on the list
              </Button>
              <span className="text-brand-300 font-mono text-xs font-bold tracking-[0.1em] uppercase">
                Try free for 3 months
              </span>
            </div>
          </Reveal>
          <Reveal delay={150} className="h-[clamp(320px,34vw,460px)] w-full overflow-hidden rounded-sm">
            <img
              src={cfoHero}
              alt="A fleet owner checking his phone and holding a tablet in front of his trucks"
              className="h-full w-full object-cover"
            />
          </Reveal>
        </section>

        <section className="bg-ink-50 px-5.5 py-24 lg:py-28">
          <div className="mx-auto max-w-(--maxw)">
            <Reveal>
              <span className="text-brand-800 font-mono text-xs font-bold tracking-[0.14em] uppercase">
                How it works
              </span>
              <h2 className="h2 mt-5 max-w-[18ch] text-pretty">Just connect what you already use</h2>
            </Reveal>

            <Reveal delay={100} className="mx-auto mt-14 max-w-275">
              {/* 4 sources, each with a short connector line dropping from it */}
              <div className="grid grid-cols-4 gap-3 max-sm:grid-cols-2">
                {CONNECTS.map((label) => (
                  <div key={label} className="grid justify-items-center gap-0">
                    <span className="border-ink-100 rounded-full border bg-white px-4 py-2 text-sm font-semibold whitespace-nowrap">
                      {label}
                    </span>
                    <span className="bg-ink-200 h-6.5 w-px" />
                  </div>
                ))}
              </div>
              {/* horizontal line bridging the 4 connectors into one */}
              <div className="grid grid-cols-[12.5%_1fr_12.5%] items-center">
                <span />
                <span className="bg-ink-200 h-px" />
                <span />
              </div>
              {/* Cai AI CFO pill, with a line in and a line out */}
              <div className="grid justify-items-center gap-0">
                <span className="bg-ink-200 h-6.5 w-px" />
                <span className="bg-ink-900 inline-flex items-center gap-2.5 rounded-full px-5.5 py-3 text-white">
                  <span className="bg-brand-500 size-1.5 rounded-full" />
                  <span className="font-mono text-xs font-bold tracking-[0.1em] uppercase">Cai AI CFO</span>
                </span>
                <span className="bg-ink-200 h-6.5 w-px" />
              </div>
              {/* horizontal line fanning back out to the 4 capabilities */}
              <div className="grid grid-cols-[12.5%_1fr_12.5%] items-center">
                <span />
                <span className="bg-ink-200 h-px" />
                <span />
              </div>
              {/* 4 connector lines dropping down to each capability */}
              <div className="grid grid-cols-4 gap-3 max-sm:grid-cols-2">
                {CAPABILITIES.map((cap) => (
                  <span key={cap.label} className="grid justify-items-center">
                    <span className="bg-ink-200 h-6.5 w-px" />
                  </span>
                ))}
              </div>
            </Reveal>

            <div className="mx-auto mt-3 grid max-w-275 grid-cols-4 gap-6 max-sm:grid-cols-2">
              {CAPABILITIES.map((cap, i) => (
                <Reveal
                  key={cap.label}
                  delay={i * 80}
                  className="flex flex-col items-center gap-2 text-center"
                >
                  <span className="text-brand-800 font-mono text-xs font-bold tracking-[0.1em] uppercase">
                    {cap.label}
                  </span>
                  <span className="text-base font-semibold tracking-[-0.01em]">{cap.body}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      </div>

      <section className="bg-white px-5.5 py-24 lg:py-32">
        <div className="mx-auto max-w-(--maxw)">
          <div className="border-ink-100 grid grid-cols-2 items-center gap-14 border-t py-16 max-lg:grid-cols-1">
            <Reveal>
              <span className="text-brand-800 font-mono text-xs font-bold tracking-[0.12em] uppercase">
                Every day
              </span>
              <h3 className="h3 mt-4 max-w-[16ch] text-pretty">Keeps a close eye on your money</h3>
              <p className="text-md text-ink-600 mt-6 max-w-135 leading-[1.6]">
                Executes approved collections and payments, and flags problems that need your attention:
                fraud, overspending, duplicate charges, potential cash shortages.
              </p>
            </Reveal>
            <Reveal delay={150} className="flex flex-col gap-3.5">
              <div className="border-ink-100 mx-2 rounded-sm border bg-white px-4 py-3.5 opacity-70">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <span className="text-ink-500 font-mono text-[10px] font-bold tracking-[0.08em] uppercase">
                    Payment sent
                  </span>
                  <span className="text-ink-500 font-mono text-[10px]">9:40 am</span>
                </div>
                <p className="text-ink-600 mt-1.5 text-sm font-semibold tracking-[-0.01em]">
                  Maintenance · Ridgeline Diesel · <span className="font-mono">$2,180</span>
                </p>
              </div>
              <div className="border-ink-200 rounded-sm border border-l-[3px] border-l-(--amber) bg-white px-7 py-6.5">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <span className="font-mono text-xs font-bold tracking-[0.08em] text-(--amber-text) uppercase">
                    Needs your ok
                  </span>
                  <span className="text-ink-500 font-mono text-xs">6:12 am</span>
                </div>
                <p className="mt-4.5 text-xl leading-[1.35] font-bold tracking-[-0.015em]">
                  Duplicate fuel charge · Truck 118 · <span className="font-mono">$412</span>
                </p>
                <div className="mt-4.5 flex flex-wrap gap-2.5">
                  <span className="bg-ink-900 rounded-sm px-5 py-2.5 text-sm font-bold text-white">
                    Dispute
                  </span>
                  <span className="border-ink-100 text-ink-600 rounded-sm border px-5 py-2.5 text-sm font-bold whitespace-nowrap">
                    It&apos;s fine
                  </span>
                </div>
              </div>
              <div className="border-ink-100 mx-2 rounded-sm border bg-white px-4 py-3.5 opacity-70">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <span className="text-ink-500 font-mono text-[10px] font-bold tracking-[0.08em] uppercase">
                    Payment received
                  </span>
                  <span className="text-ink-500 font-mono text-[10px]">Yesterday</span>
                </div>
                <p className="text-ink-600 mt-1.5 text-sm font-semibold tracking-[-0.01em]">
                  Midwest Freight Brokers · Invoice 4471 · <span className="font-mono">$6,210</span>
                </p>
              </div>
            </Reveal>
          </div>

          <div className="border-ink-100 grid grid-cols-2 items-center gap-14 border-t py-16 max-lg:grid-cols-1">
            <Reveal>
              <span className="text-brand-800 font-mono text-xs font-bold tracking-[0.12em] uppercase">
                Every month
              </span>
              <h3 className="h3 mt-4 max-w-[16ch] text-pretty">Shows you where you stand</h3>
              <p className="text-md text-ink-600 mt-6 max-w-135 leading-[1.6]">
                Builds your quarterly forecast and reports on its accuracy. Shows you cost per mile and
                contribution margin per truck, benchmarked against similar fleets.
              </p>
            </Reveal>
            <Reveal delay={150} className="border-ink-100 rounded-sm border bg-white p-6 lg:order-first">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <span className="text-brand-800 font-mono text-[11px] font-bold tracking-[0.08em] uppercase">
                  13-week cash forecast
                </span>
                <span className="text-ink-500 font-mono text-[11px]">Last forecast, within 3%</span>
              </div>
              <div className="mt-3.5 grid h-22 grid-cols-[repeat(13,minmax(0,1fr))] items-end gap-1">
                {FORECAST_BARS.map((h, i) => (
                  <span
                    key={i}
                    className={cn('block rounded-[1px]', i === 7 ? 'bg-brand-600' : 'bg-brand-200')}
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
              <div className="text-ink-500 mt-2 flex justify-between font-mono text-[10px]">
                <span>Wk 1</span>
                <span>
                  Low week 8 — <span className="text-brand-700 font-bold">$9,100</span>
                </span>
                <span>Wk 13</span>
              </div>
              <div className="border-ink-100 mt-5 flex flex-wrap items-baseline justify-between gap-4 border-t pt-5">
                <span className="text-brand-800 font-mono text-[11px] font-bold tracking-[0.08em] uppercase">
                  Per truck
                </span>
                <div className="text-ink-500 flex gap-3.5 font-mono text-[10px]">
                  <span className="flex items-center gap-1.5">
                    <span className="bg-brand-600 h-1.5 w-2.5 rounded-full" />
                    You
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="bg-ink-100 h-1.5 w-2.5 rounded-full" />
                    Similar fleets
                  </span>
                </div>
              </div>
              <div className="mt-4 flex flex-col gap-2">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-semibold">Cost per mile</span>
                  <span className="font-mono text-sm font-bold">$1.94</span>
                </div>
                <span className="bg-brand-600 block h-1.5 w-[78%] rounded-full" />
                <span className="bg-ink-100 block h-1.5 w-[86%] rounded-full" />
              </div>
              <div className="mt-3 flex flex-col gap-2">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-semibold">Contribution margin</span>
                  <span className="font-mono text-sm font-bold">31%</span>
                </div>
                <span className="bg-brand-600 block h-1.5 w-[62%] rounded-full" />
                <span className="bg-ink-100 block h-1.5 w-[48%] rounded-full" />
              </div>
            </Reveal>
          </div>

          <div className="border-ink-100 grid grid-cols-2 items-center gap-14 border-t py-16 max-lg:grid-cols-1">
            <Reveal>
              <span className="text-brand-800 font-mono text-xs font-bold tracking-[0.12em] uppercase">
                Every time you need it
              </span>
              <h3 className="h3 mt-4 max-w-[16ch] text-pretty">Helps you make the big decisions</h3>
              <p className="text-md text-ink-600 mt-6 max-w-135 leading-[1.6]">
                Plans the big money moves, like a new truck or an insurance renewal, and recommends pricing,
                hiring, equipment, and supplier changes.
              </p>
            </Reveal>
            <Reveal delay={150} className="flex flex-col gap-4">
              {QUESTIONS.map((q, i) => (
                <div
                  key={q}
                  className={cn(
                    'bg-brand-100 w-fit max-w-full rounded-xl px-5 py-4 text-base leading-[1.45] font-semibold',
                    i % 2 === 1 && 'ml-auto'
                  )}
                >
                  {q}
                </div>
              ))}
            </Reveal>
          </div>
        </div>
      </section>

      <section id="beta" className="bg-ink-50 px-5.5 py-24 lg:py-28">
        <div className="mx-auto max-w-(--maxw)">
          <Reveal>
            <span className="text-brand-800 font-mono text-xs font-bold tracking-[0.14em] uppercase">
              Beta program
            </span>
            <h2 className="h2 mt-4.5 max-w-[18ch] text-pretty">Add an AI CFO to your team</h2>
            <p className="text-ink-600 mt-5 max-w-135 text-lg leading-[1.6]">
              Try AI CFO free for your first 3 months.
            </p>
          </Reveal>
          <Reveal delay={150} className="mt-9 max-w-165">
            <HubSpotFormEmbed
              portalId={HUBSPOT_PORTAL_ID}
              formId={HUBSPOT_FORM_ID}
              region={HUBSPOT_REGION}
              onSubmitted={() => trackMarketingCta('cfo_beta_waitlist')}
            />
          </Reveal>
        </div>
      </section>

      <section id="faq" className="bg-ink-900 px-5.5 py-24 lg:py-28">
        <div className="mx-auto grid max-w-(--maxw) grid-cols-2 items-start gap-14 max-lg:grid-cols-1">
          <Reveal>
            <span className="text-brand-500 font-mono text-xs font-bold tracking-[0.14em] uppercase">
              Questions
            </span>
            <h2 className="h2 mt-4.5 max-w-[14ch] text-pretty text-white">The short answers</h2>
            <p className="mt-5 max-w-110 text-lg leading-[1.6] text-white">
              Still have questions? Call{' '}
              <a href="tel:7707650331" className="text-brand-300">
                (770) 765-0331
              </a>{' '}
              or email{' '}
              <a href="mailto:hello@cai.ai" className="text-brand-300">
                hello@cai.ai
              </a>
              .
            </p>
          </Reveal>
          <Reveal delay={100}>
            {FAQS.map((faq, i) => (
              <FaqItem
                key={faq.question}
                question={faq.question}
                answer={faq.answer}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </Reveal>
        </div>
      </section>

      <SiteFooter
        hideEdgeLines
        tagline={SITE_FOOTER_TAGLINE}
        linkColumns={SITE_FOOTER_LINK_COLUMNS}
        legalLinks={SITE_FOOTER_LEGAL_LINKS}
        note={
          <>
            <p>The Cai AI CFO is in beta. It is not accounting, tax, investment or legal advice.</p>
            <p>
              If you are having difficulty viewing or navigating the content on this website, or find a
              feature or functionality that is not fully accessible to you, please email us at{' '}
              <a href="mailto:hello@cai.ai">hello@cai.ai</a>.
            </p>
          </>
        }
      />
    </div>
  )
}
